import { useState, type FormEvent } from 'react';
import type { Accent, LoginCardProps } from './types';
import { DevPanels } from './DevPanels';

// Static class strings (no dynamic concatenation) so Tailwind's content scanner
// picks them up inside this workspace package — see each app's `@source`.
const ACCENT: Record<Accent, string> = {
  amber: 'bg-amber-500 hover:bg-amber-400 text-neutral-950',
  sky: 'bg-sky-500 hover:bg-sky-400 text-neutral-950',
  neutral:
    'bg-neutral-900 hover:bg-neutral-700 text-white dark:bg-neutral-200 dark:hover:bg-neutral-100 dark:text-neutral-950',
};

/**
 * The one login card shared by every EB app. Presentational + locally-stateful;
 * all data flows through props (no supabase/router import). EB is single-user,
 * so there is NO sign-up — only sign-in, Google, optional password reset, and
 * the dev tools. Light by default; dark via the .dark class on <html>.
 */
export function LoginCard({
  branding,
  actions,
  onAuthenticated,
  showPasswordReset = false,
  passwordResetHref,
  dev,
}: LoginCardProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const { error } = await actions.signIn(email, password);
    setSubmitting(false);
    if (error) {
      setError(error instanceof Error ? error.message : 'Sign-in failed');
      return;
    }
    onAuthenticated();
  };

  const handleGoogle = async () => {
    setError(null);
    const { error } = await actions.signInWithGoogle();
    if (error) setError(error instanceof Error ? error.message : 'Google sign-in failed');
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-white p-4 text-neutral-900 dark:bg-neutral-950 dark:text-neutral-100">
      <div className="w-full max-w-sm rounded-2xl border border-neutral-200 bg-neutral-50 p-8 shadow-xl dark:border-neutral-800 dark:bg-neutral-900">
        <h1 className="mb-1 text-2xl font-semibold">{branding.title}</h1>
        <p className="mb-6 text-sm text-neutral-500 dark:text-neutral-400">
          {branding.subtitle ?? 'Sign in with your EntityBox account.'}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm text-neutral-700 dark:text-neutral-300">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm outline-none focus:border-neutral-500 dark:border-neutral-700 dark:bg-neutral-800"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm text-neutral-700 dark:text-neutral-300">Password</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm outline-none focus:border-neutral-500 dark:border-neutral-700 dark:bg-neutral-800"
            />
          </div>

          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

          <button
            type="submit"
            disabled={submitting}
            className={`w-full rounded-lg px-3 py-2 text-sm font-medium transition disabled:opacity-60 ${ACCENT[branding.accent]}`}
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        {showPasswordReset && passwordResetHref && (
          <a
            href={passwordResetHref}
            className="mt-3 block text-center text-xs text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300"
          >
            Forgot password?
          </a>
        )}

        <div className="my-4 flex items-center gap-3 text-xs text-neutral-400 dark:text-neutral-600">
          <span className="h-px flex-1 bg-neutral-200 dark:bg-neutral-800" /> or{' '}
          <span className="h-px flex-1 bg-neutral-200 dark:bg-neutral-800" />
        </div>

        <button
          onClick={handleGoogle}
          className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm font-medium transition hover:bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-800 dark:hover:bg-neutral-700"
        >
          Continue with Google
        </button>

        {dev && <DevPanels dev={dev} />}
      </div>
    </div>
  );
}
